import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/cn';

import { Bolts } from './Bolts';

/**
 * Superficies y piezas de composicion.
 *
 * La placa la dibuja `.card` en index.css: canto de acero, esquina recta y
 * cuatro tornillos en el parque; el aspecto de siempre en la portada. Aqui
 * solo se decide cuando una placa es interactiva y cuanto aire lleva.
 */

export function Card({
  children,
  className,
  meander,
  interactive,
  as: Tag = 'div',
}: {
  children: React.ReactNode;
  className?: string;
  /** Filigrana de greca en el borde superior (solo se ve en la portada). */
  meander?: boolean;
  /** Responde al pasar por encima: solo si la placa entera es un enlace. */
  interactive?: boolean;
  as?: 'div' | 'section' | 'article' | 'li';
}) {
  return (
    <Tag
      className={cn(
        'card overflow-hidden',
        // El destello solo en las que son enlace: en una placa que no se
        // puede pulsar, una luz que la cruza al pasar el ratón promete algo
        // que no va a ocurrir.
        interactive && 'card-interactive sheen',
        className,
      )}
    >
      {meander && <div className="meander-rule meander-live" aria-hidden="true" />}
      {children}
    </Tag>
  );
}

/**
 * Titulo de pagina o de seccion. Como `h1` es el titular de la pantalla, con
 * la escala de letrero; como `h2`, el de cada bloque.
 */
export function SectionTitle({
  children,
  action,
  className,
  id,
  as: Tag = 'h2',
}: {
  children: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
  id?: string;
  as?: 'h1' | 'h2';
}) {
  return (
    <div className={cn('mb-4 flex items-center justify-between gap-3', className)}>
      <Tag
        id={id}
        className={cn(
          'font-display uppercase text-ink',
          Tag === 'h1' ? 'text-[2.5rem] leading-[0.95]' : 'text-[1.75rem] leading-none',
        )}
      >
        {children}
      </Tag>
      {action && <div className="shrink-0 text-sm">{action}</div>}
    </div>
  );
}

type Tone = 'neutral' | 'success' | 'warning' | 'error' | 'gold' | 'crimson';

const TONES: Record<Tone, string> = {
  neutral: 'bg-surface-2 text-ink-muted ring-line',
  success: 'bg-success/12 text-success ring-success/30',
  warning: 'bg-warning/12 text-warning ring-warning/30',
  error: 'bg-error/12 text-error ring-error/30',
  // Oro y carmesi dependen del mundo (index.css): en el parque, amarillo de
  // seguridad y placa invertida; en la portada, los de siempre.
  gold: 'pill-gold',
  crimson: 'pill-crimson',
};

/** Etiqueta de estado: tematica, estado de envio, categoria. */
export function Pill({
  children,
  tone = 'neutral',
  className,
}: {
  children: React.ReactNode;
  tone?: Tone;
  className?: string;
}) {
  return (
    <span
      className={cn(
        // Un anillo de 1px pesa menos que un borde y no descuadra el alto.
        // Letra, tamano, radio e interletrado salen del mundo (`.pill`):
        // etiqueta impresa condensada en el parque, capsula en la portada.
        'pill inline-flex items-center gap-1 px-2.5 py-1 uppercase ring-1 ring-inset',
        TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/**
 * Estado vacio: una placa en blanco con sus cuatro tornillos sin apretar. Es
 * el mismo objeto que una estacion, todavia sin nada serigrafiado.
 */
export function EmptyState({
  title,
  body,
  action,
  className,
}: {
  title: string;
  body?: string;
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'card plate-live-bolts flex flex-col items-center gap-3 px-6 py-12 text-center',
        className,
      )}
    >
      <Bolts set={false} />
      <p className="max-w-sm font-display text-[1.375rem] uppercase leading-tight text-ink">{title}</p>
      {body && <p className="max-w-sm text-sm leading-relaxed text-ink-muted">{body}</p>}
      {action}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <div
      className={cn('animate-pulse rounded-card bg-surface-2/70', className)}
      role="status"
      aria-label={t('a11y.loading')}
    />
  );
}

/** Barra de progreso: relleno en tinta, sin brillo. */
export function ProgressBar({
  percent,
  label,
  className,
}: {
  percent: number;
  label?: string;
  className?: string;
}) {
  const clamped = Math.min(100, Math.max(0, percent));
  return (
    <div
      className={cn('h-2 w-full overflow-hidden rounded-sm bg-surface-2 ring-1 ring-inset ring-steel', className)}
      role="progressbar"
      aria-valuenow={Math.round(clamped)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      <div
        className="h-full bg-ink transition-[width] duration-200 ease-out"
        style={{ '--target-width': `${clamped}%`, width: `${clamped}%` } as React.CSSProperties}
      />
    </div>
  );
}

/**
 * Tonos cuyo texto se lee en tinta normal: el tono solo tine el fondo. Oro y
 * carmesi traen su propia tinta desde el mundo (la placa invertida del parque
 * lleva el texto en color de suelo); forzar tinta ahi dejaba el texto del
 * mismo color que el fondo.
 */
const INK_TONES: ReadonlySet<Tone> = new Set(['neutral', 'success', 'warning', 'error']);

/** Aviso destacado: verificacion pendiente, fusion de categorias, sin conexion. */
export function Banner({
  tone = 'gold',
  children,
  action,
  className,
}: {
  tone?: Tone;
  children: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 rounded-card px-4 py-3 text-sm ring-1 ring-inset',
        TONES[tone],
        INK_TONES.has(tone) && 'text-ink',
        className,
      )}
      role="status"
    >
      <span className="flex items-center gap-2">
        <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', DOTS[tone])} aria-hidden="true" />
        {children}
      </span>
      {action}
    </div>
  );
}

const DOTS: Record<Tone, string> = {
  neutral: 'bg-ink-muted',
  success: 'bg-success',
  warning: 'bg-warning',
  error: 'bg-error',
  gold: 'dot-gold',
  crimson: 'bg-current',
};

/**
 * Casilla de cifra con su etiqueta. Va dentro de `StatBoard`: las cifras de
 * un perfil se imprimen en una sola placa, no en tarjetas sueltas.
 */
export function Stat({
  label,
  value,
  hint,
  tone,
  className,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'success' | 'warning';
  className?: string;
}) {
  return (
    <div className={cn('bg-surface px-4 py-3.5', className)}>
      <dt className="font-display text-[13px] font-semibold uppercase tracking-[0.06em] text-ink-muted">
        {label}
      </dt>
      <dd
        className={cn(
          'tabular mt-1.5 font-display text-[2rem] font-bold leading-none',
          tone === 'warning' && 'text-warning',
          tone === 'success' && 'text-success',
        )}
      >
        {value}
      </dd>
      {hint && <p className="mt-1 text-xs text-ink-muted">{hint}</p>}
    </div>
  );
}

/** Tablero de cifras: casillas `Stat` sobre una placa, con reglas de acero. */
export function StatBoard({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <Card className={className}>
      <dl className="grid grid-cols-2 gap-[2px] bg-steel sm:grid-cols-4">{children}</dl>
    </Card>
  );
}
